/**
 * Right-hand audit log for the admin Results tab. Merges all rounds' events
 * into a single chronological feed (newest first) so an admin can scan the
 * full game like a tail of a structured log.
 */

import { useMemo } from 'react';
import type { AdminFprResultsResponse, AdminFprResultsEvent } from '@swtorbit/shared-socket-events';
import { AuditLogShell } from '../results';

interface Props {
  results: AdminFprResultsResponse;
}

const TYPE_ICON: Record<AdminFprResultsEvent['type'], string> = {
  team_entered: '🚪',
  boss_killed: '💀',
  team_finished: '🏁',
  team_dnf: '🚫',
};

function fmtUtc(ms: number): string {
  return new Date(ms).toISOString().replace('T', ' ').slice(0, 19);
}

export function FprResultsAuditLog({ results }: Props) {
  const allEvents = useMemo(() => {
    const merged: AdminFprResultsEvent[] = [];
    for (const r of results.rounds) merged.push(...r.events);
    merged.sort((a, b) => b.utcMs - a.utcMs);
    return merged;
  }, [results]);

  return (
    <AuditLogShell eventCount={allEvents.length}>
      {allEvents.map((e, idx) => (
        <div
          key={`${e.utcMs}-${e.teamId}-${e.type}-${idx}`}
          className="d-flex gap-xs align-items-center"
        >
          <span className="text-slate" style={{ minWidth: '11.5em' }}>{fmtUtc(e.utcMs)}</span>
          <span className="badge badge--pill ff-display">R{e.roundNumber}</span>
          <span>{TYPE_ICON[e.type] ?? '•'}</span>
          <span className="text-white flex-1">{e.label}</span>
          {e.detail && <span className="text-cyan">{e.detail}</span>}
        </div>
      ))}
    </AuditLogShell>
  );
}
